// client/src/components/UserCollections.js

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosInstance';
import { jwtDecode } from 'jwt-decode';
import ProxiedImage from './ProxiedImage';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import {
  Box,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Chip,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MessageIcon from '@mui/icons-material/Message';
import AppBarHeader from './AppBarHeader';
import CollectionValueSummary from './CollectionValueSummary';
import BulkCexUpdate from './BulkCexUpdate';

const UserCollections = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [collections, setCollections] = useState({
    games: [],
    consoles: [],
    legoSets: [],
    movies: [],
    music: [],
    books: [],
    boardGames: [],
    tradingCards: [],
    manga: [],
    comics: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState('');
  const [isRemoteUser, setIsRemoteUser] = useState(false);
  const [remoteInstance, setRemoteInstance] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Get feature flags
  const { featureFlags } = useFeatureFlags();

  // Get current user info for AppBarHeader
  const token = localStorage.getItem('token');
  const userDecoded = token ? jwtDecode(token) : null;
  const userId = userDecoded ? userDecoded.id : null;
  const isAdmin = userDecoded ? userDecoded.isAdmin : false;

  const filterItems = (items, searchTerm) => {
    if (!searchTerm) return items;
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    return items.filter(item => {
      const searchableFields = Object.values(item).map(value => 
        value ? String(value).toLowerCase() : ''
      );
      return searchableFields.some(field => field.includes(lowerSearchTerm));
    });
  };

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        console.log('Fetching collections for user:', id);
        
        // Fetch user profile first to get username
        const userResponse = await axiosInstance.get(`/users/${id}/profile`);
        setUsername(userResponse.data.username || 'User');
        if (userResponse.data.isRemote) {
          setIsRemoteUser(true);
          setRemoteInstance(userResponse.data.remoteInstance || '');
          setLoading(false);
          return;
        }

        // Create an array of promises for the basic collections
        // Use Promise.allSettled to handle individual failures gracefully
        const collectionPromises = [
          axiosInstance.get(`/games/user/${id}`).catch(err => ({ data: [], error: err })),
          axiosInstance.get(`/consoles/user/${id}`).catch(err => ({ data: [], error: err })),
          axiosInstance.get(`/lego/user/${id}`).catch(err => ({ data: [], error: err }))
        ];
        
        // Add movie collection request if feature flag is enabled
        const moviePromise = featureFlags.enableMoviesFeature ? 
          axiosInstance.get(`/movies/user/${id}`).catch(err => ({ data: [], error: err })) : 
          Promise.resolve({ data: [] });
        collectionPromises.push(moviePromise);
        
        // Add music collection request if feature flag is enabled
        const musicPromise = featureFlags.enableMusicFeature ?
          axiosInstance.get(`/music/user/${id}`).catch(err => ({ data: [], error: err })) :
          Promise.resolve({ data: [] });
        collectionPromises.push(musicPromise);

        const booksPromise = featureFlags.enableBooksFeature ?
          axiosInstance.get(`/books/user/${id}`).catch(err => ({ data: [], error: err })) :
          Promise.resolve({ data: [] });
        collectionPromises.push(booksPromise);

        const boardGamesPromise = featureFlags.enableBoardGamesFeature ?
          axiosInstance.get(`/board-games/user/${id}`).catch(err => ({ data: [], error: err })) :
          Promise.resolve({ data: [] });
        collectionPromises.push(boardGamesPromise);

        const tradingCardsPromise = featureFlags.enableTradingCardsFeature ?
          axiosInstance.get(`/trading-cards/user/${id}`).catch(err => ({ data: [], error: err })) :
          Promise.resolve({ data: [] });
        collectionPromises.push(tradingCardsPromise);

        const mangaPromise = featureFlags.enableMangaFeature ?
          axiosInstance.get(`/manga/user/${id}`).catch(err => ({ data: [], error: err })) :
          Promise.resolve({ data: [] });
        collectionPromises.push(mangaPromise);

        const comicsPromise = featureFlags.enableComicsFeature ?
          axiosInstance.get(`/comics/user/${id}`).catch(err => ({ data: [], error: err })) :
          Promise.resolve({ data: [] });
        collectionPromises.push(comicsPromise);

        // Fetch all collections in parallel
        const [gamesResponse, consolesResponse, legoResponse, moviesResponse, musicResponse, booksResponse, boardGamesResponse, tradingCardsResponse, mangaResponse, comicsResponse] =
          await Promise.all(collectionPromises);

        setCollections({
          games: gamesResponse.data || [],
          consoles: consolesResponse.data || [],
          legoSets: legoResponse.data || [],
          movies: moviesResponse.data || [],
          music: musicResponse.data || [],
          books: booksResponse.data || [],
          boardGames: boardGamesResponse.data || [],
          tradingCards: tradingCardsResponse.data || [],
          manga: mangaResponse.data || [],
          comics: comicsResponse.data || [],
        });
      } catch (err) {
        console.error('Detailed error:', err);
        setError('Failed to load collections.');
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, [id]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderGameCollection = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search games..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Image</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Platform</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(collections.games, searchTerm).length > 0 ? (
            filterItems(collections.games, searchTerm).map((game) => (
              <TableRow key={game._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    sx={{ width: 60, height: 60 }}
                  >
                    {game.imageUrl || game.background_image ? (
                      <ProxiedImage 
                        src={game.imageUrl || game.background_image} 
                        alt={game.name || game.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      (game.name || game.title || '?')[0]
                    )}
                  </Avatar>
                </TableCell>
                <TableCell>{game.name || game.title}</TableCell>
                <TableCell>{game.platform}</TableCell>
                <TableCell>
                  {game.completed ? 'Completed' : game.played ? 'Played' : 'Not Played'}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center">
                {searchTerm ? 'No games found matching your search' : 'No games in collection'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderConsoleCollection = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search consoles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Logo</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Generation</TableCell>
            <TableCell>Alternative Name</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(collections.consoles, searchTerm).length > 0 ? (
            filterItems(collections.consoles, searchTerm).map((console) => (
              <TableRow key={console._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    src={console.platformLogo || console.imageUrl}
                    alt={console.name}
                    sx={{ width: 60, height: 60 }}
                  />
                </TableCell>
                <TableCell>{console.name}</TableCell>
                <TableCell>{console.generation}</TableCell>
                <TableCell>{console.alternative_name}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center">
                {searchTerm ? 'No consoles found matching your search' : 'No consoles in collection'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderMovieCollection = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search movies..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Poster</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Director</TableCell>
            <TableCell>Year</TableCell>
            <TableCell>Format</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(collections.movies, searchTerm).length > 0 ? (
            filterItems(collections.movies, searchTerm).map((movie) => (
              <TableRow key={movie._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    sx={{ width: 60, height: 60 }}
                  >
                    {movie.imageUrl ? (
                      <ProxiedImage 
                        src={movie.imageUrl} 
                        alt={movie.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      (movie.title || '?')[0]
                    )}
                  </Avatar>
                </TableCell>
                <TableCell>{movie.title}</TableCell>
                <TableCell>{movie.director || 'Unknown'}</TableCell>
                <TableCell>{movie.year || 'Unknown'}</TableCell>
                <TableCell>
                  <Chip 
                    label={movie.format || 'Unknown'} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">
                {searchTerm ? 'No movies found matching your search' : 'No movies in collection'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderMusicCollection = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search music..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Cover</TableCell>
            <TableCell>Album</TableCell>
            <TableCell>Artist</TableCell>
            <TableCell>Year</TableCell>
            <TableCell>Format</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(collections.music, searchTerm).length > 0 ? (
            filterItems(collections.music, searchTerm).map((album) => (
              <TableRow key={album._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    sx={{ width: 60, height: 60 }}
                  >
                    {album.imageUrl ? (
                      <ProxiedImage 
                        src={album.imageUrl} 
                        alt={album.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      (album.name || '?')[0]
                    )}
                  </Avatar>
                </TableCell>
                <TableCell>{album.name}</TableCell>
                <TableCell>{album.artist}</TableCell>
                <TableCell>{album.year || 'Unknown'}</TableCell>
                <TableCell>
                  <Chip 
                    label={album.format || 'Unknown'} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">
                {searchTerm ? 'No music found matching your search' : 'No music in collection'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderBookCollection = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search books..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Cover</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Author</TableCell>
            <TableCell>Year</TableCell>
            <TableCell>Format</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(collections.books, searchTerm).length > 0 ? (
            filterItems(collections.books, searchTerm).map((book) => (
              <TableRow key={book._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    sx={{ width: 60, height: 60 }}
                  >
                    {book.imageUrl ? (
                      <ProxiedImage
                        src={book.imageUrl}
                        alt={book.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      (book.name || '?')[0]
                    )}
                  </Avatar>
                </TableCell>
                <TableCell>{book.name}</TableCell>
                <TableCell>{book.author || 'Unknown'}</TableCell>
                <TableCell>{book.year || 'Unknown'}</TableCell>
                <TableCell>
                  <Chip label={book.format || 'Unknown'} size="small" variant="outlined" />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">
                {searchTerm ? 'No books found matching your search' : 'No books in collection'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderBoardGameCollection = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search board games..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Cover</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Players</TableCell>
            <TableCell>Playtime</TableCell>
            <TableCell>Genre</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(collections.boardGames, searchTerm).length > 0 ? (
            filterItems(collections.boardGames, searchTerm).map((game) => (
              <TableRow key={game._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    sx={{ width: 60, height: 60 }}
                  >
                    {game.imageUrl ? (
                      <ProxiedImage
                        src={game.imageUrl}
                        alt={game.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      (game.name || '?')[0]
                    )}
                  </Avatar>
                </TableCell>
                <TableCell>{game.name}</TableCell>
                <TableCell>
                  {game.minPlayers && game.maxPlayers
                    ? `${game.minPlayers}–${game.maxPlayers}`
                    : game.minPlayers || game.maxPlayers || 'Unknown'}
                </TableCell>
                <TableCell>
                  {game.playingTime ? `${game.playingTime} min` : 'Unknown'}
                </TableCell>
                <TableCell>
                  <Chip label={game.genre || game.category || 'Unknown'} size="small" variant="outlined" />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">
                {searchTerm ? 'No board games found matching your search' : 'No board games in collection'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderLegoCollection = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search LEGO sets..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Image</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Set Number</TableCell>
            <TableCell>Theme</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(collections.legoSets, searchTerm).length > 0 ? (
            filterItems(collections.legoSets, searchTerm).map((set) => {
              console.log('Rendering LEGO set:', set);
              return (
                <TableRow key={set._id}>
                  <TableCell>
                    <Avatar
                      variant="rounded"
                      src={set.set_img_url || set.imageUrl}
                      alt={set.name}
                      sx={{ width: 60, height: 60 }}
                    />
                  </TableCell>
                  <TableCell>{set.name}</TableCell>
                  <TableCell>{(set.setNum || set.set_num) ? (set.setNum || set.set_num).split('-')[0] : 'N/A'}</TableCell>
                  <TableCell>{set.theme || set.themeId}</TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center">
                {searchTerm ? 'No LEGO sets found matching your search' : 'No LEGO sets in collection'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderTradingCardsCollection = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search trading cards..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Image</TableCell>
            <TableCell>Card Name</TableCell>
            <TableCell>Game</TableCell>
            <TableCell>Set</TableCell>
            <TableCell>Condition</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(collections.tradingCards, searchTerm).length > 0 ? (
            filterItems(collections.tradingCards, searchTerm).map((card) => (
              <TableRow key={card._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    src={card.imageUrl}
                    alt={card.name}
                    sx={{ width: 60, height: 60 }}
                  />
                </TableCell>
                <TableCell>{card.name}</TableCell>
                <TableCell>{card.game}</TableCell>
                <TableCell>{card.setName || '—'}</TableCell>
                <TableCell>{card.condition}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">
                {searchTerm ? 'No trading cards found matching your search' : 'No trading cards in collection'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderMangaCollection = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search manga..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Cover</TableCell>
            <TableCell>Series</TableCell>
            <TableCell>Vol.</TableCell>
            <TableCell>Author</TableCell>
            <TableCell>Demographic</TableCell>
            <TableCell>Condition</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(collections.manga, searchTerm).length > 0 ? (
            filterItems(collections.manga, searchTerm).map((item) => (
              <TableRow key={item._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    src={item.imageUrl}
                    alt={item.name}
                    sx={{ width: 48, height: 68 }}
                  />
                </TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.volume}</TableCell>
                <TableCell>{item.author || '—'}</TableCell>
                <TableCell>{item.demographic || '—'}</TableCell>
                <TableCell>{item.condition || '—'}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center">
                {searchTerm ? 'No manga found matching your search' : 'No manga in collection'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  const renderComicsCollection = () => (
    <Paper sx={{ width: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search comics..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Cover</TableCell>
            <TableCell>Series</TableCell>
            <TableCell>Issue</TableCell>
            <TableCell>Publisher</TableCell>
            <TableCell>Condition</TableCell>
            <TableCell>Grade</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterItems(collections.comics, searchTerm).length > 0 ? (
            filterItems(collections.comics, searchTerm).map((item) => (
              <TableRow key={item._id}>
                <TableCell>
                  <Avatar
                    variant="rounded"
                    src={item.imageUrl}
                    alt={`${item.series} #${item.issueNumber}`}
                    sx={{ width: 48, height: 68 }}
                  />
                </TableCell>
                <TableCell>{item.series}</TableCell>
                <TableCell>#{item.issueNumber}</TableCell>
                <TableCell>{item.publisher || '—'}</TableCell>
                <TableCell>{item.condition || '—'}</TableCell>
                <TableCell>{item.isGraded && item.grade ? `${item.gradingCompany} ${item.grade}` : '—'}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center">
                {searchTerm ? 'No comics found matching your search' : 'No comics in collection'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (isRemoteUser) {
    return (
      <div>
        <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>← Back</Button>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{username}</strong> is a member of{remoteInstance ? ` ${remoteInstance}` : ' another instance'}.
            Their collections are hosted on their home instance and cannot be viewed from here.
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <AppBarHeader 
        isAdmin={isAdmin} 
        userId={userId} 
        onLogout={handleLogout}
      />
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {!loading && !error && userId === id && (
          <>
            <CollectionValueSummary userId={id} />
            <BulkCexUpdate
              userId={id}
              onUpdateComplete={() => { window.location.reload(); }}
            />
          </>
        )}
        <Typography variant="h4" gutterBottom>
          {username}'s Collections
        </Typography>
        {(() => {
          const tabs = [
            { label: 'Games', render: renderGameCollection, enabled: true },
            { label: 'Consoles', render: renderConsoleCollection, enabled: true },
            { label: 'LEGO Sets', render: renderLegoCollection, enabled: true },
            { label: 'Movies', render: renderMovieCollection, enabled: !!featureFlags.enableMoviesFeature },
            { label: 'Music', render: renderMusicCollection, enabled: !!featureFlags.enableMusicFeature },
            { label: 'Books', render: renderBookCollection, enabled: !!featureFlags.enableBooksFeature },
            { label: 'Board Games', render: renderBoardGameCollection, enabled: !!featureFlags.enableBoardGamesFeature },
            { label: 'Trading Cards', render: renderTradingCardsCollection, enabled: !!featureFlags.enableTradingCardsFeature },
            { label: 'Manga', render: renderMangaCollection, enabled: !!featureFlags.enableMangaFeature },
            { label: 'Comics', render: renderComicsCollection, enabled: !!featureFlags.enableComicsFeature },
          ].filter(t => t.enabled);
          return (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" aria-label="collection tabs">
                  {tabs.map(t => <Tab key={t.label} label={t.label} />)}
                </Tabs>
              </Box>
              {tabs[activeTab]?.render() || null}
            </>
          );
        })()}
      </Container>
    </div>
  );
};

export default UserCollections;
